import { motion } from 'framer-motion';
import { GlassWater } from 'lucide-react';
import { useWater } from '@/hooks/useWater';
import { useTelegram } from '@/hooks/useTelegram';

/** Стандартный стакан. Все бытовые ориентиры («8 стаканов в день») считают по нему. */
const GLASS_ML = 250;

/**
 * Вода за день — рядом стаканов.
 *
 * Почему стаканы, а не миллилитры: человек не мыслит объём в цифрах, он мыслит
 * «сколько раз я пил». Ряд стаканчиков читается мгновенно и без счёта в уме,
 * а заодно сам показывает, сколько осталось.
 *
 * Тап по пустому доливает ДО него, а не по одному: если забыл отметить три раза,
 * догнать надо одним движением, иначе человек бросит отмечать вовсе.
 * Тап по последнему полному — отмена, поэтому отдельная кнопка возврата не нужна.
 */
export function WaterCard() {
  const { ml, foodMl, total, goal, add } = useWater();
  const { haptic } = useTelegram();

  const glasses = Math.max(4, Math.min(12, Math.round(goal / GLASS_ML)));
  const filled = Math.min(glasses, Math.round(ml / GLASS_ML));
  const done = total >= goal;
  // Свечение по заполнению: на пустой строке его нет вовсе — незачем требовать
  // внимания у того, кто сегодня ещё не пил.
  const glow = Math.min(1, total / goal);

  const tap = (index: number) => {
    if (index < filled) {
      haptic('medium');
      add(-1); // снимаем последнюю отметку
    } else {
      haptic('light');
      add((index + 1 - filled) * GLASS_ML);
    }
  };

  return (
    <div
      className="relative bg-bg-card rounded-xl px-3 py-2.5 mb-4 border transition-colors"
      style={{
        borderColor: done ? 'rgba(61,216,255,0.45)' : 'rgba(34,43,51,1)',
        boxShadow: glow > 0 ? `0 0 ${10 + glow * 14}px rgba(61,216,255,${0.1 + glow * 0.22})` : undefined,
      }}
    >
      <div className="flex items-center gap-2">
        <span className="text-[11px] text-ink-dim shrink-0">Вода</span>

        <div className="flex-1 flex items-center justify-center gap-[3px]">
          {Array.from({ length: glasses }).map((_, i) => {
            const isFull = i < filled;
            return (
              <motion.button
                key={i}
                onClick={() => tap(i)}
                whileTap={{ scale: 0.82 }}
                aria-label={isFull ? `Отменить стакан ${i + 1}` : `Отметить ${i + 1} стаканов`}
                className="p-0.5 -m-0.5"
              >
                <GlassWater
                  className={`w-[15px] h-[15px] transition-colors ${
                    isFull ? 'text-sky-glow' : 'text-ink-dim/35'
                  }`}
                  style={isFull ? { filter: 'drop-shadow(0 0 5px rgba(61,216,255,0.75))' } : undefined}
                  aria-hidden
                />
              </motion.button>
            );
          })}
        </div>

        <span className="text-[11px] tabular-nums shrink-0 text-right leading-tight">
          <span className={done ? 'text-sky-glow font-semibold' : 'text-ink'}>
            {(total / 1000).toFixed(1)}
          </span>
          <span className="text-ink-dim">/{(goal / 1000).toFixed(1)}л</span>
          {foodMl > 0 && (
            <span className="block text-[9px] text-ink-dim">
              +{(foodMl / 1000).toFixed(1)} из еды
            </span>
          )}
        </span>
      </div>
    </div>
  );
}
